import React, { useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowClockwise, Buildings, DeviceMobile, EnvelopeSimple, NotePencil, Phone, WarningCircle, type Icon } from '@phosphor-icons/react';
import { Chip, ImmoButton, SegmentedControl, SlidePanel, WidgetGrid } from '../../../../components/immobilien/ui';
import { EIG_PANEL_KOPF, eigWidgets, istEigPanel } from '../../../../components/immobilien/eigentuemerWidgets';
import { heuteIso, initialen } from '../../../../lib/immobilien/format';
import { gruppen, type EigGruppe, type EigPanel } from '../../../../lib/eigentuemer/rollen';
import { EIG_STATUS, PORTAL_ANZEIGE, statusText } from '../../../../lib/eigentuemer/liste';
import { kicker, portalAus, portfolioWerte, zusammenfassung } from '../../../../lib/eigentuemer/detail';
import { useEigentuemerDetail } from '../../../../lib/eigentuemer/useEigentuemerDetail';
import { EigSeitenKontext, type EigSeitenWert } from './EigentuemerSeitenKontext';
import { EigentuemerWidget } from './EigentuemerWidget';
import { EigentuemerPanelInhalt } from './panels/EigentuemerPanelInhalt';

/** Schnellaktion im Kopf (Prototyp: 76×64, Radius 18, --fill2) – wie auf der Mieterseite. */
function Schnellaktion({ icon: IconKomp, label, href, onClick, deaktiviert }: { icon: Icon; label: string; href?: string; onClick?: () => void; deaktiviert?: string }) {
  const stil: React.CSSProperties = {
    width: 'clamp(62px, 18vw, 76px)', height: 64, borderRadius: 18, gap: 4, fontSize: 11, fontWeight: 700, color: 'var(--ink)', background: 'var(--fill2)',
    border: 'none', textDecoration: 'none', opacity: deaktiviert ? 0.45 : 1,
  };
  const inhalt = <><IconKomp weight="fill" size={20} color="var(--acc)" />{label}</>;
  if (href && !deaktiviert) return <a href={href} className="immo-hover-flaeche flex flex-col items-center justify-center" style={stil}>{inhalt}</a>;
  return (
    <button type="button" onClick={deaktiviert ? undefined : onClick} aria-disabled={deaktiviert ? true : undefined} title={deaktiviert}
      className={`flex flex-col items-center justify-center ${deaktiviert ? 'cursor-not-allowed' : 'immo-hover-flaeche cursor-pointer'}`} style={stil}>
      {inhalt}
    </button>
  );
}

const istGruppe = (v: string | null): v is EigGruppe => v === 'alle' || v === 'port' || v === 'fin' || v === 'weg' || v === 'verw' || v === 'komm';

/**
 * Eigentümer-Detail (Handoff Eigentümer §6.5, Screens 10–14, 63, 64): Kopf-Karte, Schnellaktionen,
 * Gruppenfilter nach Rolle und Widgets; ein Klick öffnet ?panel=<key>.
 */
export default function EigentuemerSeite({ eigentuemerId, onNavigateToObjekt }: { eigentuemerId: string; onNavigateToObjekt?: (id: string) => void }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { zustand, neuLaden } = useEigentuemerDetail(eigentuemerId);
  const daten = zustand.status === 'ok' ? zustand.daten : null;
  const heute = heuteIso();

  const rohPanel = params.get('panel');
  const rohGruppe = params.get('grp');

  const oeffnePanel = useCallback((key: EigPanel) => {
    setParams(p => { const n = new URLSearchParams(p); n.set('panel', key); return n; });
    window.scrollTo({ top: 0 });
  }, [setParams]);
  const verlassen = useRef<(() => void) | null>(null);
  const registriereVerlassen = useCallback((f: (() => void) | null) => { verlassen.current = f; }, []);
  const schliessePanel = useCallback(() => {
    verlassen.current = null;
    setParams(p => { const n = new URLSearchParams(p); n.delete('panel'); return n; });
  }, [setParams]);
  const zurueck = useCallback(() => { if (verlassen.current) verlassen.current(); else schliessePanel(); }, [schliessePanel]);
  const setzeGruppe = (g: EigGruppe) => setParams(p => { const n = new URLSearchParams(p); if (g === 'alle') n.delete('grp'); else n.set('grp', g); return n; });
  const zumObjekt = useCallback((id: string) => {
    if (onNavigateToObjekt) onNavigateToObjekt(id); else navigate(`/admin/objekte/${encodeURIComponent(id)}`);
  }, [navigate, onNavigateToObjekt]);

  const wert: EigSeitenWert | null = useMemo(() => {
    if (!daten || zustand.status !== 'ok') return null;
    const e = daten.eigentuemer;
    const email = (e.email ?? '').trim();
    const portal = portalAus(daten.portalStatus, email);
    return {
      daten,
      fehler: zustand.fehler,
      name: daten.zeile.name,
      email,
      telefon: (e.telefon ?? '').trim() || (e.mobil ?? '').trim(),
      portal,
      portfolio: portfolioWerte(daten.zeile.zuordnungen),
      heute,
      zusammenfassung: zusammenfassung({
        name: daten.zeile.name, zeile: daten.zeile, heute, portal,
        miteigentuemer: new Set(daten.miteigentuemer.map(m => m.id)).size, termine: daten.termine.length,
      }),
      oeffnePanel, neuLaden, registriereVerlassen, schliessePanel, zumObjekt,
    };
  }, [daten, zustand, heute, oeffnePanel, neuLaden, registriereVerlassen, schliessePanel, zumObjekt]);

  if (zustand.status === 'laedt') {
    return (
      <div aria-busy="true" aria-label="Eigentümer wird geladen" className="flex flex-col" style={{ gap: 16 }}>
        <div className="animate-pulse" style={{ height: 128, borderRadius: 30, background: 'var(--fill2)' }} />
        <div className="animate-pulse" style={{ height: 40, width: 480, maxWidth: '100%', borderRadius: 16, background: 'var(--fill2)' }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gridAutoRows: 150, gap: 14 }}>
          {Array.from({ length: 8 }, (_, i) => <div key={i} className="animate-pulse" style={{ borderRadius: 26, background: 'var(--fill2)' }} />)}
        </div>
      </div>
    );
  }
  if (zustand.status === 'fehlt' || zustand.status === 'fehler' || !wert) {
    return (
      <div role="alert" className="immo-glas flex flex-col items-center text-center" style={{ borderRadius: 28, padding: '48px 20px', gap: 12 }}>
        <WarningCircle weight="fill" size={36} color="var(--bad)" />
        <span style={{ fontSize: 18, fontWeight: 800 }}>{zustand.status === 'fehlt' ? 'Eigentümer nicht gefunden' : 'Eigentümer konnte nicht geladen werden'}</span>
        {zustand.status === 'fehler' && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>{zustand.meldung}</span>}
        <div className="flex" style={{ gap: 8 }}>
          <ImmoButton onClick={() => navigate('/admin/eigentuemer')}>Alle Eigentümer</ImmoButton>
          {zustand.status === 'fehler' && <ImmoButton art="akzent" icon={ArrowClockwise} onClick={neuLaden}>Erneut versuchen</ImmoButton>}
        </div>
      </div>
    );
  }

  const z = wert.daten.zeile;
  const w = z.werte;
  const e = wert.daten.eigentuemer;
  const st = EIG_STATUS[z.status];
  const portal = PORTAL_ANZEIGE[wert.portal];
  const gruppenListe = gruppen(w);
  const gruppe: EigGruppe = istGruppe(rohGruppe) && gruppenListe.some(g => g.key === rohGruppe) ? rohGruppe : 'alle';
  // Panels, die zur Rolle nicht passen, gibt es für diesen Eigentümer nicht
  const panel = istEigPanel(rohPanel) && eigWidgets(w, 'alle').some(d => d.key === rohPanel) ? rohPanel : null;
  const kopf = panel ? EIG_PANEL_KOPF[panel] : null;
  const fehlerBereiche = Object.keys(wert.fehler);
  const objektIds = z.objektIds;
  const einheitenText = `${w.nEinheiten} ${w.nEinheiten === 1 ? 'Einheit' : 'Einheiten'}`;
  const objektChip = objektIds.length === 1 ? `${z.zuordnungen[0]?.objektName ?? 'Objekt'}${w.nEinheiten ? ` · ${einheitenText}` : ''}` : `${objektIds.length} Objekte · ${einheitenText}`;
  const zuObjekten = () => { if (objektIds.length === 1 && objektIds[0]) zumObjekt(objektIds[0]); else oeffnePanel('objekte'); };

  return (
    <EigSeitenKontext.Provider value={wert}>
      <div className="flex flex-col" style={{ gap: 16 }}>
        <section className="immo-glas immo-side-in relative flex flex-wrap items-center overflow-hidden" style={{ borderRadius: 30, padding: 22, gap: 18 }} aria-label="Eigentümer">
          <span className="immo-akzent flex items-center justify-center shrink-0" style={{ width: 72, height: 72, borderRadius: '50%', fontSize: 24, fontWeight: 800 }}>
            {initialen(wert.name)}
          </span>
          <div className="flex flex-col min-w-0" style={{ flex: '1 1 260px', gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink3)' }}>
              {kicker({ anrede: e.anrede, firma: z.firma, rollen: w.rollen, status: z.status, beginn: z.beginn, endetAm: z.endetAm })}
            </span>
            <h1 className="m-0" style={{ fontSize: 34, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.05, overflowWrap: 'anywhere' }}>{wert.name}</h1>
            <div className="flex flex-wrap items-center" style={{ gap: 6 }}>
              {objektIds.length > 0 ? (
                <button type="button" onClick={zuObjekten} title={objektIds.length === 1 ? 'Zum Objekt' : 'Objekte & Rollen'} className="immo-hover-flaeche flex items-center cursor-pointer"
                  style={{ height: 26, padding: '0 10px', borderRadius: 13, gap: 5, border: 'none', fontSize: 12, fontWeight: 700, background: 'var(--fill2)', color: 'var(--ink)' }}>
                  <Buildings weight="fill" size={13} color="var(--acc)" />{objektChip}
                </button>
              ) : <Chip ton="neutral" groesse="s">Kein Objekt zugeordnet</Chip>}
              <Chip ton={st.ton} groesse="s" punkt>{statusText(z)}</Chip>
              <Chip ton={portal.ton} groesse="s" icon={DeviceMobile}>{portal.label}</Chip>
            </div>
          </div>
          <div className="flex flex-wrap" style={{ gap: 8 }}>
            <Schnellaktion icon={Phone} label="Anrufen" href={wert.telefon ? `tel:${wert.telefon.replace(/[^\d+]/g, '')}` : undefined} deaktiviert={wert.telefon ? undefined : 'Keine Telefonnummer hinterlegt'} />
            <Schnellaktion icon={EnvelopeSimple} label="E-Mail" href={wert.email ? `mailto:${wert.email}` : undefined} deaktiviert={wert.email ? undefined : 'Keine E-Mail hinterlegt'} />
            <Schnellaktion icon={DeviceMobile} label="Portal" onClick={() => oeffnePanel('portal')} />
            <Schnellaktion icon={NotePencil} label="Notiz" onClick={() => oeffnePanel('notizen')} />
          </div>
        </section>

        {fehlerBereiche.length > 0 && (
          <div role="status" className="flex items-center flex-wrap" style={{ gap: 10, padding: '10px 14px', borderRadius: 16, background: 'var(--warn-soft)', color: 'var(--warn)', fontSize: 13, fontWeight: 600 }}>
            <WarningCircle weight="fill" size={18} />
            Einige Bereiche konnten nicht geladen werden ({fehlerBereiche.join(', ')}).
            <button type="button" onClick={neuLaden} className="cursor-pointer" style={{ border: 'none', background: 'transparent', color: 'inherit', fontWeight: 800, textDecoration: 'underline' }}>Erneut laden</button>
          </div>
        )}

        {kopf && panel ? (
          <SlidePanel key={panel} titel={kopf.titel} icon={kopf.icon} onZurueck={zurueck}>
            <EigentuemerPanelInhalt panel={panel} />
          </SlidePanel>
        ) : (
          <>
            <SegmentedControl art="akzent" ariaLabel="Widget-Gruppen" wert={gruppe} onChange={setzeGruppe} className="self-start" segmente={gruppenListe} />
            <WidgetGrid minSpalte={160} zeile={150} abstand={14}
              elemente={eigWidgets(w, gruppe).map(d => ({ key: d.key, groesse: d.groesse, inhalt: <EigentuemerWidget def={d} s={wert} onOeffnen={() => oeffnePanel(d.key)} /> }))} />
          </>
        )}
      </div>
    </EigSeitenKontext.Provider>
  );
}
